import { Injectable } from '@angular/core';
import { ActionDisabledCondition, ActionItemOptions, UserAction } from '@mediahaven/shared';
import { ApiFragment, NavigationLink, RecordType } from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { MediaHavenActionType } from '../../../entities/action-types.entity';
import { ActionLinkService } from '../../abstract/action-link.service.interface';

@Injectable({ providedIn: 'root' })
export class OpenEditActionService extends ActionLinkService {
    protected override action: MediaHavenActionType = MediaHavenActionType.EDIT;

    // Services

    /* Edit
     * Available when:
     *  * User has edit rights
     *  * Item is NOT deleted
     *  * Item is not a representation
     *
     * Disabled when:
     *   * Has non-editable status
     *   * Is a collection with candidate children
     *   * Item has errors
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return of(this.userActionBuilder(record));
    }

    public override userActionBuilder(record: ApiFragment): UserAction {
        const hasEditRights: boolean = record.hasEditRights();
        const isDeleted: boolean = record.isDeleted();
        const isRepresentation: boolean = record.getRecordType() === RecordType.Representation;

        const hasEditableStatus: boolean = record.hasEditableStatus();
        const hasCandidateChildren: boolean = record.isCandidateParent();
        const hasErrors: boolean = record.hasSevereErrors();

        const availableConditions: boolean[] = [hasEditRights, !isDeleted, !isRepresentation];
        const disableConditions: ActionDisabledCondition[] = [
            { condition: !hasEditableStatus, tooltip: 'general.blocked-status' },
            { condition: hasCandidateChildren, tooltip: 'general.candidate-children' },
            { condition: hasErrors, tooltip: 'general.blocked-status' }
        ];

        return new UserAction(availableConditions, disableConditions);
    }

    public getLabelKey(target: ApiFragment): string {
        return 'actions.edit';
    }

    public override getIcon(target: ApiFragment, actionItemOptions: ActionItemOptions): string | undefined {
        return target.isConcept() && actionItemOptions.withIcon ? 'edit' : undefined;
    }

    protected getLink(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<NavigationLink> {
        const route = actionItemOptions.shareContext
            ? record.getShareEditUrl(actionItemOptions.shareContext)
            : record.getEditUrl();

        const state: any = {
            queryIteratorStack: actionItemOptions.queryIteratorStack
        };

        return of({
            route,
            extras: { state }
        });
    }
}
